import React, { useState } from 'react'
import { useUsersApi } from 'src/api/db/apiUsers'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui'
import { Avatar, AvatarImage } from 'src/shared/components/ui/avatar'
import { cn } from 'src/shared/components/ui/utils'
import { capitalize } from 'src/shared/utils'
import { Badge } from 'src/shared/components/ui/badge'
import { Shield } from 'lucide-react'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'

interface UserProfileButtonProps {
  onClick: () => void
  colorVariant?: 'light' | 'dark'
  showName?: boolean
  layout?: 'header' | 'sidebar'
}

export const UserProfileButton = ({
  onClick,
  colorVariant = 'light',
  showName = true,
  layout = 'header',
}: UserProfileButtonProps) => {
  const { user } = useUsersApi()
  const { organization } = useOrgsApi({})
  const [logoError, setLogoError] = useState(false)
  const isDark = colorVariant === 'dark'
  const isAdmin = useIsAdmin()
  const { t } = useLocalization()

  const getUserName = () => {
    if (!user?.data) return null
    const firstName = capitalize(user.data.first_name)
    const lastName = capitalize(user.data.last_name)
    if (firstName || lastName) {
      return [firstName, lastName].filter(Boolean).join(' ')
    }
    return user.data.email?.split('@')[0] || null
  }

  const getUserInitials = () => {
    if (!user?.data) return 'G'
    const { first_name, last_name, email } = user.data
    if (first_name && last_name)
      return `${first_name[0]}${last_name[0]}`.toUpperCase()
    if (first_name) return first_name[0].toUpperCase()
    if (last_name) return last_name[0].toUpperCase()
    if (email) return email[0].toUpperCase()
    return 'G'
  }

  const companyName = organization?.data?.name || null
  const companyDisplayName =
    companyName && companyName !== 'Anon'
      ? companyName
      : t('Not signed in', 'Ej inloggad')
  const companyLogoUrl = organization?.data?.chosen_logo_url || null
  const userName = getUserName()
  const userInitials = getUserInitials()

  React.useEffect(() => {
    setLogoError(false)
  }, [companyLogoUrl])

  const showLogo = companyLogoUrl && !logoError
  const isSidebar = layout === 'sidebar'
  const shouldShowName = isSidebar || showName

  return (
    <Button
      className={cn(
        'h-auto font-normal',
        isSidebar
          ? 'w-full justify-start rounded-lg px-3 py-2.5 hover:bg-gray-50'
          : 'p-1.5'
      )}
      variant="ghost"
      onClick={onClick}
    >
      <Row className={cn('items-center gap-3', isSidebar && 'w-full min-w-0')}>
        {isAdmin && !isSidebar && (
          <Badge variant="orange" className="flex items-center gap-1.5 mr-2">
            <Shield className="w-3 h-3" />
            {t('Admin', 'Admin')}
          </Badge>
        )}

        <Avatar
          size="md"
          className="h-8 w-8 flex-shrink-0 rounded-md"
        >
          {showLogo && (
            <AvatarImage
              src={companyLogoUrl}
              alt={companyName || 'Company logo'}
              className="h-full w-full object-contain p-1"
              onError={() => setLogoError(true)}
            />
          )}
          <div className="flex h-full w-full items-center justify-center rounded-md bg-muted text-sm font-medium">
            {userInitials}
          </div>
        </Avatar>

        {shouldShowName && (
          <Column
            className={cn(
              'min-w-0 flex-1 items-start gap-0.5',
              !isSidebar && 'hidden lg:flex'
            )}
          >
            <Row className="w-full min-w-0 items-center gap-1.5">
              <span
                className={cn(
                  'truncate font-semibold text-sm leading-tight',
                  isDark ? 'text-white' : 'text-neutral-900'
                )}
              >
                {userName || t('Log in', 'Logga in')}
              </span>
              {isAdmin && isSidebar && (
                <Badge variant="orange" className="flex shrink-0 items-center gap-1 px-1.5 py-0">
                  <Shield className="h-3 w-3" />
                </Badge>
              )}
            </Row>

            <span
              className={cn(
                'w-full truncate text-xs leading-tight',
                isDark ? 'text-neutral-400' : 'text-neutral-500'
              )}
            >
              {companyDisplayName}
            </span>
          </Column>
        )}
      </Row>
    </Button>
  )
}
